import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { Predictions } from "src/shared/lib/amplify";
import { isEmpty } from "ramda"
import { TranslateTextCommand } from "@aws-sdk/client-translate";
import { getTranslateClient, executeWithRetry } from "src/utils/translationHelper";


export interface TranslationState {
    originalJSON: Record<string, any>;
    translatedJSON: Record<string, any>;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
    translationsMap: Record<string, Record<string, any>>; // Stores translation by language
    // translationsMap: Record<string, Record<string, string>>; // Stores translation by language
    currentLanguage: string;
}

const initialState: TranslationState = {
    originalJSON: {},
    translatedJSON: {},
    status: "idle",
    error: null,
    translationsMap: {}, // Stores only the current language translations
    currentLanguage: "en", // Default to English
}

const DELIMITER = "\n";

const languageFontMap = new Map([
    ["ko", ["cc_custom_terminology"]]
]);

const collectTexts = (obj: Record<string, any>, path = ""): [string, string][] => {
    let texts: [string, string][] = []

    for (const key in obj) {
        const fullPath = path ? `${path}.${key}` : key
        if (typeof obj[key] === "object") {
            texts = texts.concat(collectTexts(obj[key], fullPath))
        } else {
            if (!texts.some(([, value]) => value === obj[key]))
                texts.push([fullPath, obj[key]])
        }
    }
    return texts
}

// setTexts based on the original JSON path and translated values
const setTexts = (obj: Record<string, any>, texts: [string, string][]) => {
    const textMap = new Map(texts)
    const setNestedTexts = (obj: Record<string, any>, path = "") => {
        for (const key in obj) {
            const fullPath = path ? `${path}.${key}` : key
            if (typeof obj[key] === "object") {
                setNestedTexts(obj[key], fullPath)
            } else if (textMap.has(fullPath)) {
                const translatedValue = textMap.get(fullPath)
                if (translatedValue) {
                    obj[key] = translatedValue
                }
            }
        }
    }
    setNestedTexts(obj)
}

// Function to check if input is a valid JSON object
const isValidJSON = (input: any): boolean => {
    return typeof input === "object" && input !== null && !Array.isArray(input);
}

export const translateJSON = createAsyncThunk(
  "translation/translateJSON",
  async (
    {
      json,
      pageNameKey,
      targetLanguage: overrideTargetLanguage
    }: {
      json: Record<string, any>
      pageNameKey?: any
      targetLanguage?: string
    },
    thunkAPI
  ) => {
    // Check if input JSON is empty
    if (isEmpty(json)) {
      return json
    }

    const state = thunkAPI.getState() as any
    const targetLanguage =
      overrideTargetLanguage || state.userData?.selectedLanguage || "en"
    // If the target language is English, return the original JSON without translation
    if (targetLanguage === "en") {
      return json;
    }

    // Validate input JSON
    if (!isValidJSON(json)) {
      console.error("translateJSON Error: Input must be a valid JSON object");
      return thunkAPI.rejectWithValue("Invalid input: Expected JSON object");
    }

    const { translationCombinedSlice: { translationsMap } } = state

    // Create a more specific cache key that includes pageNameKey if provided
    const cacheKey = pageNameKey
      ? `${pageNameKey}_${JSON.stringify(json)}`
      : JSON.stringify(json)

    // Return cached translations if available
    if (
      targetLanguage !== "en" &&
      translationsMap[targetLanguage]?.[cacheKey]
    ) {
      return translationsMap[targetLanguage][cacheKey] // Return cached translation if available
    }

    const texts = collectTexts(json)
    const uniqueValues = Array.from(new Set(texts.map(([, value]) => value))) // Get unique values for translation

    // Filter out empty, null, undefined, or whitespace-only strings
    const validTexts = uniqueValues.filter(
      (text) => text !== null && text !== undefined
    )

    // If no valid texts to translate, return original JSON
    if (validTexts.length === 0) {
      return json
    }

    try {
      const encoder = new TextEncoder()
      const textsToTranslateStr = uniqueValues.join(DELIMITER)
      const totalSizeInBytes = encoder.encode(textsToTranslateStr).length

      const splitTexts = []
      let currentChunk = ""
      let currentChunkBytes = 0

      // Split text into chunks if the total size exceeds 10,000 bytes
      if (totalSizeInBytes > 9900) {
        const lines = textsToTranslateStr.split(DELIMITER)
        for (const line of lines) {
          const lineBytes = encoder.encode(line + DELIMITER)
          if (currentChunkBytes + lineBytes.length > 9900) {
            if (currentChunk) {
              splitTexts.push(currentChunk) // Add the current chunk to the array
            }
            currentChunk = line + DELIMITER // Start a new chunk
            currentChunkBytes = lineBytes.length
          } else {
            currentChunk += line + DELIMITER // Add line to current chunk
            currentChunkBytes += lineBytes.length
          }
        }

        if (currentChunk) {
          splitTexts.push(currentChunk) // Add the last chunk
        }
      } else {
        splitTexts.push(textsToTranslateStr) // No need to split if total size is within limit
      }

      // Perform translations in parallel for each chunk of text
      const client = await getTranslateClient()
      if (!client) {
        return json
      }
      const translationResults = await Promise.all(
        splitTexts.map(async (text) => {
          try {
            if (languageFontMap.has(targetLanguage)) {
              const command = new TranslateTextCommand({
                Text: text,
                SourceLanguageCode: "en",
                TargetLanguageCode: targetLanguage,
                TerminologyNames: languageFontMap.get(targetLanguage) || []
              })

              const result = await executeWithRetry(() => client.send(command))
              return result.TranslatedText
            } else {
              const result = await Predictions.convert({
                translateText: {
                  source: {
                    text,
                    language: "en"
                  },
                  targetLanguage
                }
              })
              return result.text
            }
          } catch (error) {
            console.error("Translation error:", error)
            throw error
          }
        })
      )

      const combinedTranslation = translationResults.join("") // Combine all translated chunks
      const transResult = combinedTranslation.split(DELIMITER)

      // Save new translations in the state while keeping the original structure
      const newTranslations: Record<string, string> = {}

      // Map unique values to their translations
      uniqueValues.forEach((value, index) => {
        newTranslations[value] = transResult[index]
      })

      // Create the final output with original length
      const finalOutput = texts.map(([, value]) => newTranslations[value])

      // Populate translated object with original structure
      const translatedTexts = texts.map(
        ([path], index) => [path, finalOutput[index]] as [string, string]
      )
      const translatedObj = JSON.parse(JSON.stringify(json)) // Deep copy original object
      setTexts(translatedObj, translatedTexts)

      // Reset translationsMap to store only the current language translations
      const updatedTranslationsMap = {
        ...translationsMap,
        [targetLanguage]: {
          ...translationsMap[targetLanguage],
          [cacheKey]: translatedObj // Cache the translated object
        }
      }

      // Update the state with the new translations and current language
      thunkAPI.dispatch(updateTranslationsMap(updatedTranslationsMap))
      thunkAPI.dispatch(setCurrentLanguage(targetLanguage))

      const newTranlatedObj = {
        ...translatedObj,
        ...(pageNameKey && { pageNameKey })
      }

      return newTranlatedObj
    } catch (error: any) {
      console.log(error)
      const message = error.message
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const translationCombinedSlice = createSlice({
  name: "translation",
  initialState,
  reducers: {
    setOriginalJSON: (state, action: PayloadAction<Record<string, any>>) => {
      state.originalJSON = action.payload
    },
    updateTranslationsMap: (
      state,
      action: PayloadAction<Record<string, Record<string, any>>>
    ) => {
      state.translationsMap = action.payload // Overwrite with the new language translations
    },
    setCurrentLanguage: (state, action: PayloadAction<string>) => {
      state.currentLanguage = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(translateJSON.pending, (state) => {
        state.status = "loading"
      })
      .addCase(translateJSON.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.translatedJSON = {
          ...state.translatedJSON,
          ...action.payload
        }
      })
      .addCase(translateJSON.rejected, (state, action: PayloadAction<any>) => {
        state.status = "failed"
        state.error = action.payload
      });
  },
});

export const { setOriginalJSON, updateTranslationsMap, setCurrentLanguage } = translationCombinedSlice.actions;
export default translationCombinedSlice.reducer;
